import {
  CustomParameters,
  PurchasableProduct,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import { useEffect } from 'react';
import styled from 'styled-components';

import { PackageDefinition } from '#/client/models';
import { ProductPicker } from '#/components/ProductPicker/ProductPicker';
import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import { handleScreenView } from '#/plugins/analytics/analyticsUtils';
import { PlanPickerSubText } from '#/plugins/paywall/components';
import { tb } from '#/store/translation/translation.store';

import { Actions, PurchaseFlowStep } from '../PlanPurchaseFlow.types';
import { StepWrapper } from '../StepWrapper';
import { usePickProductStep } from './PickProductStep.hook';

export interface PickProductStepProps {
  actions: Actions;
  currentStep: PurchaseFlowStep.PICK;
  customParameters?: CustomParameters;
  packageDefinition: PackageDefinition;
  purchasableProducts: PurchasableProduct[];
  onProductPicked: (product: PurchasableProduct) => void;
}

const SubText = styled(PlanPickerSubText)`
  font: var(--fontStyleFootnote);
`;

export const PickProductStep = ({
  actions,
  currentStep,
  purchasableProducts,
  packageDefinition,
  customParameters,
  onProductPicked,
}: PickProductStepProps) => {
  const {
    disclaimer,
    renderContent,
    pickerItems,
    handleOnContinue,
    onCancel,
    cancelButtonTranslationKey,
    availableTabs,
    renderItem,
  } = usePickProductStep({
    onProductPicked,
    customParameters,
    purchasableProducts,
    packageDefinition,
  });

  /* In case there are no available products, show the empty state
     logic moved from V1, we might need to rework this later */
  useEffect(() => {
    const screenTitle = tb('iap.selectYourPlan');

    handleScreenView({
      screenType: ScreenType.ProductPicker,
      screenHeading: screenTitle,
      screenTitle,
    });

    if (!purchasableProducts?.length) {
      actions.handleError(
        new OneError(Errors.PURCHASE_OFFERS_LOADING_NO_OFFERS_TO_PURCHASE)
      );
    }
  }, [purchasableProducts?.length, actions]);

  if (!renderContent || !pickerItems || !purchasableProducts?.length) {
    return null;
  }

  return (
    <StepWrapper step={currentStep}>
      <ProductPicker
        availableTabs={availableTabs}
        cancelButtonTranslationKey={cancelButtonTranslationKey}
        items={pickerItems}
        onCancel={onCancel}
        onContinue={handleOnContinue}
        renderItem={renderItem}
      />
      {disclaimer && (
        <SubText $isCentered $isTextCentered>
          {disclaimer}
        </SubText>
      )}
    </StepWrapper>
  );
};

PickProductStep.displayName = 'PickProductStep';
